/**
 * Clap Manager V2 - Firebase-based with localStorage fallback
 * This replaces the original clapManager.js
 */

import * as tomatoClapService from '../services/tomatoClapService';

// Cache for offline support
const countCache = {
  claps: {},
  lastFetch: {}
};

// Event system for reactive updates
const dispatchCountChange = (userId, count) => {
  document.dispatchEvent(new CustomEvent('clapCountChanged', {
    detail: { userId, count }
  }));
};

/**
 * Get clap count with Firebase primary, localStorage fallback
 * @param {string} userId - The user ID
 * @returns {Promise<number>} Current clap count
 */
export const getClapCount = async (userId) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Try Firebase first
    const counts = await tomatoClapService.getUserCounts(userId);
    
    // Cache the result
    countCache.claps[userId] = counts.clapCount;
    countCache.lastFetch[userId] = Date.now();
    
    // Also store in localStorage for offline
    localStorage.setItem(`claps_${userId}`, counts.clapCount.toString());
    localStorage.setItem(`lastClapReset_${userId}`, new Date().toISOString());
    
    return counts.clapCount;
  } catch (error) {
    
    // Fallback to localStorage
    const stored = localStorage.getItem(`claps_${userId}`);
    if (stored !== null) {
      const count = parseInt(stored, 10);
      if (!isNaN(count) && count >= 0) {
        return count;
      }
    }
    
    // Default if nothing stored
    return 10;
  }
};

/**
 * Get clap count synchronously from cache
 * Use this for immediate UI updates
 * @param {string} userId - The user ID
 * @returns {number} Cached clap count
 */
export const getClapCountSync = (userId) => {
  userId = userId || 'anonymous_user';
  
  // Check cache first
  if (countCache.claps[userId] !== undefined) {
    return countCache.claps[userId];
  }
  
  // Check localStorage
  const stored = localStorage.getItem(`claps_${userId}`);
  if (stored !== null) {
    const count = parseInt(stored, 10);
    if (!isNaN(count) && count >= 0) {
      countCache.claps[userId] = count;
      return count;
    }
  }
  
  // Default
  return 10;
};

/**
 * Use a clap
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New clap count
 */
export const useClap = async (userId) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Optimistically update cache and localStorage
    const currentCount = getClapCountSync(userId);
    if (currentCount <= 0) {
      throw new Error('No claps available');
    }
    
    const newCount = currentCount - 1;
    countCache.claps[userId] = newCount;
    localStorage.setItem(`claps_${userId}`, newCount.toString());
    dispatchCountChange(userId, newCount);
    
    // Try Firebase
    const firebaseCount = await tomatoClapService.useClap(userId);
    
    // Update with Firebase result if different
    if (firebaseCount !== newCount) {
      countCache.claps[userId] = firebaseCount;
      localStorage.setItem(`claps_${userId}`, firebaseCount.toString());
      dispatchCountChange(userId, firebaseCount);
    }
    
    return firebaseCount;
  } catch (error) {
    console.error('Error using clap:', error);
    
    // If Firebase fails, stick with local update
    const localCount = getClapCountSync(userId);
    if (localCount > 0) {
      const newCount = localCount - 1;
      localStorage.setItem(`claps_${userId}`, newCount.toString());
      countCache.claps[userId] = newCount;
      dispatchCountChange(userId, newCount);
      return newCount;
    }
    
    throw error;
  }
};

/**
 * Refund a clap
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New clap count
 */
export const refundClap = async (userId) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Optimistically update
    const currentCount = getClapCountSync(userId);
    const newCount = currentCount + 1;
    countCache.claps[userId] = newCount;
    localStorage.setItem(`claps_${userId}`, newCount.toString());
    dispatchCountChange(userId, newCount);
    
    // Try Firebase
    const firebaseCount = await tomatoClapService.refundClap(userId);
    
    // Update with Firebase result
    if (firebaseCount !== newCount) {
      countCache.claps[userId] = firebaseCount;
      localStorage.setItem(`claps_${userId}`, firebaseCount.toString());
      dispatchCountChange(userId, firebaseCount);
    }
    
    return firebaseCount;
  } catch (error) {
    console.error('Error refunding clap:', error);
    // Keep optimistic update
    return getClapCountSync(userId);
  }
};

/**
 * Add purchased claps
 * @param {string} userId - The user ID
 * @param {number} amount - Number to add
 * @returns {Promise<number>} New clap count
 */
export const addPurchasedClaps = async (userId, amount) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Update Firebase
    const newCount = await tomatoClapService.addPurchasedClaps(userId, amount);
    
    // Update cache and localStorage
    countCache.claps[userId] = newCount;
    localStorage.setItem(`claps_${userId}`, newCount.toString());
    dispatchCountChange(userId, newCount);
    
    return newCount;
  } catch (error) {
    console.error('Error adding purchased claps:', error);
    throw error;
  }
};

/**
 * Check if user has claps available
 * @param {string} userId - The user ID
 * @returns {boolean} Whether user has claps
 */
export const hasClapsAvailable = (userId) => {
  return getClapCountSync(userId) > 0;
};

/**
 * Initialize clap counts on app load
 * @param {string} userId - The user ID
 */
export const initializeClapCounts = async (userId) => {
  if (!userId) return;
  
  try {
    // Fetch from Firebase
    await getClapCount(userId);
  } catch (error) {
    console.error('Error initializing clap counts:', error);
  }
};

/**
 * Subscribe to real-time updates
 * @param {string} userId - The user ID
 * @param {function} callback - Callback when count changes
 * @returns {function} Unsubscribe function
 */
export const subscribeToClapCount = (userId, callback) => {
  if (!userId) return () => {};
  
  // Subscribe to Firebase changes
  const unsubscribe = tomatoClapService.subscribeToUserCounts(userId, (counts) => {
    if (counts && counts.clapCount !== undefined) {
      countCache.claps[userId] = counts.clapCount;
      localStorage.setItem(`claps_${userId}`, counts.clapCount.toString());
      dispatchCountChange(userId, counts.clapCount);
      callback(counts.clapCount);
    }
  });
  
  return unsubscribe;
};

// Re-export functions that don't need changes
export function canClap(userId, elementId) {
  userId = userId || 'anonymous_user';
  
  // Check if we've clapped at this element very recently (within 1 second)
  const clapTimeKey = `lastClap_${elementId}_${userId}`;
  const lastClapTime = localStorage.getItem(clapTimeKey);
  
  if (lastClapTime) {
    const lastClap = parseInt(lastClapTime, 10);
    const now = Date.now();
    const elapsedMs = now - lastClap;
    
    // If less than 1 second has passed, prevent the clap
    if (elapsedMs < 1000) {
      return false;
    }
  }
  
  // Store the current time
  localStorage.setItem(clapTimeKey, Date.now().toString());
  
  return true;
}

// For backward compatibility
export const setClapCount = async (userId, count) => {
  console.warn('setClapCount is deprecated. Counts are managed by Firebase.');
  // Store locally for offline support
  localStorage.setItem(`claps_${userId}`, count.toString());
  countCache.claps[userId] = count;
  dispatchCountChange(userId, count);
  return count;
};

export const resetClapCount = async (userId) => {
  console.warn('resetClapCount is deprecated. Daily resets are handled by Firebase.');
  return getClapCount(userId);
};